<script setup lang="ts">
import { AppText } from '@shared/ui/typography'
import { LoaderOverlay } from '@shared/ui/loaders'
import { AppDatePicker, NativeSelect } from '@shared/ui/fields'
import { CardAdmin } from '@shared/ui/cards'
import { BtnPrimary, BtnSecondary } from '@shared/ui/buttons'

import { useLessonCreateStore } from '../../model/lessons/create/store'
import { useLessonCreateSingle } from '../../model/lessons/lesson-create-single.hook'
import LessonFormLayout from './LessonFormLayout.vue'
import LessonBalanceAvailable from './LessonBalanceAvailable.vue'
import LessonTutorBlock from './LessonTutorBlock.vue'
import ModalLessonCreateTutor from './modals/ModalLessonCreateTutor.vue'

defineOptions({
  name: 'LessonCreateSingle',
})

const store = useLessonCreateStore()

const {
  refCanCreate,
  refTypeOptions,
  refType,
  refBalance,
  refIsBalanceShown,
  refDate,
  refMinDate,
  refStartOptions,
  refStartTime,
  refDurationOptions,
  refDuration,
  refCanChangeTutor,
  refIsSaving,
  refLessonsUrl,
  setType,
  setDate,
  setStartTime,
  setDuration,
  openTutorModal,
  openBalance,
  submit,
} = useLessonCreateSingle()
</script>

<template>
  <CardAdmin
    title="Add new lesson"
    class="lesson-create-single"
    fullwidth
  >
    <template #header>
      <div
        v-if="refCanCreate"
        class="lesson-create-single__controls"
      >
        <BtnSecondary
          text="Go back"
          color="black"
          :to="refLessonsUrl"
        />
        <BtnPrimary
          text="Add lesson"
          color="emerald"
          :disabled="refIsSaving"
          @click="submit"
        />
      </div>
    </template>
    <LessonFormLayout v-if="refCanCreate">
      <template #main>
        <div class="lesson-create-single__form">
          <AppText name="body1-medium">
            Lesson details
          </AppText>
          <div class="lesson-create-single__grid">
            <NativeSelect
              class="lesson-create-single__type"
              name="lesson_type"
              label="Lesson type"
              :options="refTypeOptions"
              :value="refType"
              :disabled="refIsSaving"
              @change="setType"
            />
            <LessonBalanceAvailable
              v-if="refIsBalanceShown && refBalance !== null"
              class="lesson-create-single__balance"
              :value="refBalance"
              :disabled="refIsSaving"
              @open="openBalance"
            />
            <AppDatePicker
              class="lesson-create-single__date"
              label="Date"
              :value="refDate"
              :min-date="refMinDate"
              :disabled="refIsSaving"
              @change="setDate"
            />
            <div class="lesson-create-single__time">
              <NativeSelect
                class="lesson-create-single__slot"
                name="time_start"
                label="Time"
                :options="refStartOptions"
                :value="refStartTime"
                :disabled="refIsSaving"
                @change="setStartTime"
              />
              <AppText
                class="lesson-create-single__separator"
                name="body2-medium"
                color="slate-900"
              >
                to
              </AppText>
              <NativeSelect
                class="lesson-create-single__slot"
                name="time_end"
                :options="refDurationOptions"
                :value="refDuration"
                :disabled="refIsSaving"
                @change="setDuration"
              />
            </div>
          </div>
        </div>
      </template>
      <template #aside>
        <LessonTutorBlock
          :name="null"
          schedule-url=""
          schedule-disabled
          change-text="Select tutor"
          :can-change="refCanChangeTutor"
          @change="openTutorModal"
        />
      </template>
    </LessonFormLayout>
    <AppText
      v-else
      class="lesson-create-single__denied"
      name="body2"
      color="slate-500"
    >
      You do not have permission to add lessons.
    </AppText>
    <LoaderOverlay v-if="refIsSaving && !store.isTutorModalOpen" />
    <ModalLessonCreateTutor v-if="store.isTutorModalOpen" />
  </CardAdmin>
</template>

<style scoped lang="scss" src="./LessonCreateSingle.scss" />
